import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import PageLoader from '../components/PageLoader';
import BusinessCard from '../components/BusinessCard';
import api from '../utils/api';
import { isUserLoggedIn } from '../utils/auth';
import { Heart, Search, ArrowLeft } from 'lucide-react';
import { showSuccess, showError } from '../utils/toast';

const FavouritesPage = () => {
    const [businesses, setBusinesses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [favouriteIds, setFavouriteIds] = useState(new Set());
    const navigate = useNavigate();
    const loggedIn = isUserLoggedIn();

    useEffect(() => {
        if (!loggedIn) {
            navigate('/login', { state: { from: { pathname: '/favourites' } } });
            return;
        }

        const fetchFavourites = async () => {
            try {
                const response = await api.get('/favourites');
                const favs = response.data.favourites || [];
                setBusinesses(favs);
                setFavouriteIds(new Set(favs.map(b => (b._id || '').toString())));
            } catch (error) {
                console.error('Error fetching favourites:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchFavourites();
    }, [loggedIn, navigate]);

    const handleRemoveFavourite = async (businessId) => {
        try {
            await api.delete(`/favourites/${businessId}`);
            setBusinesses(prev => prev.filter(b => b._id !== businessId));
            setFavouriteIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(businessId);
                return newSet;
            });
            showSuccess('Removed from favourites');
        } catch (error) {
            showError('Could not remove from favourites');
        }
    };

    const handleAddFavourite = () => {
        // Toggle logic handled in BusinessCard
    };

    if (loading) {
        return <PageLoader message="Loading your favourites..." fullScreen />;
    }

    return (
        <>
            <SEO title="My Favourites" description="Your saved businesses and collections." noindex />

            <div className="min-h-screen bg-gray-50/50 dark:bg-gray-950 pb-20 md:pb-12">
                {/* Premium Header */}
                <div className="bg-gradient-to-r from-red-500 to-pink-600 dark:from-red-900 dark:to-pink-900 text-white shadow-lg relative overflow-hidden">
                    <div className="absolute inset-0 bg-black/10"></div>
                    <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-3xl"></div>
                    <div className="absolute top-10 left-10 w-20 h-20 bg-white/10 rounded-full blur-xl"></div>

                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-24 md:py-12 relative z-10">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                            <div className="flex items-center gap-4">
                                <button onClick={() => navigate(-1)} className="md:hidden p-2 -ml-2 text-white/80 hover:text-white rounded-full hover:bg-white/10 transition-colors">
                                    <ArrowLeft className="w-6 h-6" />
                                </button>
                                <div>
                                    <h1 className="text-3xl md:text-4xl font-bold flex items-center gap-3">
                                        <Heart className="w-8 h-8 fill-white text-white" />
                                        My Favourites
                                    </h1>
                                    <p className="text-red-100 mt-2 text-lg">
                                        You have {businesses.length} saved {businesses.length === 1 ? 'business' : 'businesses'} in your collection
                                    </p>
                                </div>
                            </div>

                            {businesses.length > 0 && (
                                <div className="bg-white/20 backdrop-blur-md rounded-2xl p-4 flex items-center gap-4 border border-white/20">
                                    <div className="text-center">
                                        <div className="text-2xl font-bold">{businesses.length}</div>
                                        <div className="text-xs text-red-100 uppercase tracking-wider font-semibold">Items</div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-20">
                    {businesses.length > 0 ? (
                        <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-xl overflow-hidden min-h-[50vh]">
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-0 md:gap-8 divide-y md:divide-y-0 divide-gray-100 dark:divide-gray-800 p-0 md:p-8">
                                {businesses.map((business) => (
                                    <div key={business._id} className="md:transform md:transition-all md:hover:-translate-y-1">
                                        <BusinessCard
                                            business={business}
                                            favouriteIds={favouriteIds}
                                            onAddFavourite={handleAddFavourite}
                                            onRemoveFavourite={handleRemoveFavourite}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-xl p-8 md:p-16 text-center">
                            <div className="inline-flex h-24 w-24 items-center justify-center rounded-full bg-red-50 dark:bg-red-900/10 mb-6 animate-bounce-slow">
                                <Heart className="h-12 w-12 text-red-300 dark:text-red-800" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">Your collection is empty</h2>
                            <p className="text-gray-500 dark:text-gray-400 mb-8 max-w-md mx-auto text-lg leading-relaxed">
                                Start exploring Digital Fatehabad. When you find a business you love, tap the heart icon to save it here.
                            </p>
                            <Link to="/search" className="inline-flex items-center gap-2 px-8 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold shadow-lg shadow-red-500/20 transition-all hover:scale-105">
                                <Search className="w-5 h-5" />
                                Explore Businesses
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default FavouritesPage;
